import React, { useEffect, useMemo, useState } from 'react';
import {
  collection,
  doc,
  getDoc,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import { db } from '../../../firebase/config';
import {
  Check,
  Shield,
  UserCog,
  Loader2,
  Search,
  AlertTriangle,
  Save,
  X,
  RefreshCw,
} from 'lucide-react';

const card = "bg-white border border-slate-200/70 rounded-2xl shadow-sm";
const subCard = "bg-slate-50 border border-slate-200/70 rounded-2xl shadow-sm";
const field =
  "w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-sm " +
  "placeholder:text-slate-400 " +
  "hover:border-slate-400 " +
  "focus:outline-none focus:ring-4 focus:ring-blue-500/15 focus:border-blue-500";

const tabBase =
  "px-4 py-1.5 text-sm rounded-full border transition font-semibold";
const tabActive = "bg-blue-600 text-white border-blue-600 shadow-sm";
const tabIdle = "bg-white text-slate-700 border-slate-300 hover:bg-slate-50";

const badge =
  "inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold border";

const warnBox =
  "mb-4 p-4 rounded-2xl border border-amber-200 bg-amber-50 text-amber-900 flex items-start gap-2 shadow-sm";


// --- Sincronizar colección profesionales --- //
async function syncProfesional(uid, email, nombre, worker) {
  const ref = doc(db, 'profesionales', uid);

  if (worker) {
    // Crear o actualizar profesional activo
    await setDoc(
      ref,
      {
        uid,
        nombre: nombre || email || 'Sin nombre',
        correo: email || '',
        activo: true,
        actualizadoEn: serverTimestamp(),
      },
      { merge: true }
    );
  } else {
    // Marcar como inactivo si ya no es worker
    await setDoc(
      ref,
      {
        activo: false,
        actualizadoEn: serverTimestamp(),
      },
      { merge: true }
    );
  }
}

// ---------- UI helpers ----------
const RoleBadge = ({ label, active }) => (
  <span
    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${
      active
        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
        : "bg-slate-100 text-slate-600 border-slate-200"
    }`}
  >
    {active ? <Check size={14} /> : <X size={14} />} {label}
  </span>
);

// ---------- Desktop row ----------
const RoleRowDesktop = ({
  item,
  draft,
  setDraft,
  hasRoleDoc,
  canDemote,
  saving,
  onSaveRoles,
  dirty,
}) => {
  return (
    <tr className="border-b border-slate-200/70 last:border-none hover:bg-slate-50/70 transition-colors">
      <td className="py-2 px-4">
        <div className="font-medium text-slate-900">
          {item.displayName || item.nombre || '—'}
        </div>
        <div className="text-xs text-slate-500">{item.email || '—'}</div>
        <div className="text-[10px] text-slate-400">uid: {item.uid || '—'}</div>

        {!item.email && (
          <div className="mt-1 text-[11px] text-amber-700">
            Perfil en <code>usuario/</code> faltante
          </div>
        )}
        {!item.uid && (
          <div className="mt-1 text-[11px] text-rose-700">
            UID faltante en perfil (pida que inicie sesión o complete manualmente)
          </div>
        )}
        {item.hasProfesional && (
          <div className="mt-1 text-[11px] text-emerald-700">
            Profesional: {item.especialidad || '—'} {item.comuna ? `· ${item.comuna}` : ''}
          </div>
        )}
      </td>

      {/* Estado actual */}
      <td className="py-2 px-4">
        {hasRoleDoc ? (
          <div className="flex flex-wrap gap-2">
            <RoleBadge label="administrador" active={!!item.roles?.admin} />
            <RoleBadge label="trabajador" active={!!item.roles?.worker} />
            <RoleBadge label="usuario" active={!!item.roles?.user} />
          </div>
        ) : (
          <span className="text-xs text-slate-500">Sin documento de roles (se asume user)</span>
        )}
      </td>

      {/* Editar */}
      <td className="py-2 px-4">
        <div className="flex flex-wrap items-center gap-4">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="h-4 w-4 accent-blue-600"
              checked={draft.admin}
              onChange={(e) => setDraft((d) => ({ ...d, admin: e.target.checked }))}
              disabled={saving || (!canDemote && !draft.admin)}
              title={!canDemote && !draft.admin ? 'No puedes retirar el único administrador' : ''}
            />
            Administrador
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="h-4 w-4 accent-blue-600"
              checked={draft.worker}
              onChange={(e) => setDraft((d) => ({ ...d, worker: e.target.checked }))}
              disabled={saving}
            />
            Trabajador
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="h-4 w-4 accent-blue-600"
              checked={draft.user}
              onChange={(e) => setDraft((d) => ({ ...d, user: e.target.checked }))}
              disabled={saving}
            />
            Usuario
          </label>
        </div>
      </td>

      {/* Acciones */}
      <td className="py-2 px-4 text-right">
        <div className="flex flex-wrap items-center justify-end gap-2">
          {dirty && (
            <button
              onClick={() =>
                setDraft({
                  admin: !!item.roles?.admin,
                  worker: !!item.roles?.worker,
                  // si no hay roles en la BD, volvemos al default user: true
                  user: item.roles ? !!item.roles.user : true,
                })
              }
              className="px-3 py-1.5 text-sm font-semibold border border-slate-300 rounded-xl bg-white hover:bg-slate-50"
              disabled={saving}
            >
              <RefreshCw className="inline-block mr-1" size={16} />
              Reset
            </button>
          )}
          <button
            onClick={() => onSaveRoles(item.uid, draft)}
            className={`px-3 py-1.5 text-sm rounded-xl font-semibold text-white flex items-center gap-1 shadow-sm ${
              dirty && item.uid
                ? 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800'
                : 'bg-blue-300 cursor-not-allowed'
            }`}
            disabled={!dirty || saving || !item.uid}
            title={!item.uid ? 'Este usuario no tiene uid en su perfil' : ''}
          >
            {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
            Guardar
          </button>
        </div>
        {hasRoleDoc && !canDemote && (
          <div className="mt-1 text-[11px] text-amber-600 flex items-center gap-1">
            <AlertTriangle size={12} /> Este es el único administrador; no puedes quitarte el rol.
          </div>
        )}
      </td>
    </tr>
  );
};

// ---------- Mobile card ----------
const RoleCardMobile = ({
  item,
  draft,
  setDraft,
  hasRoleDoc,
  canDemote,
  saving,
  onSaveRoles,
  dirty,
}) => {
  return (
    <div className="rounded-xl border p-4 mb-3 bg-white">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-semibold text-slate-900">
            {item.displayName || item.nombre || '—'}
          </div>
          <div className="text-xs text-slate-500">{item.email || '—'}</div>
          <div className="text-[10px] text-slate-400">uid: {item.uid || '—'}</div>

          {!item.email && (
            <div className="mt-1 text-[11px] text-amber-700">
              Perfil en <code>usuarios/</code> faltante
            </div>
          )}
          {!item.uid && (
            <div className="mt-1 text-[11px] text-rose-700">
              UID faltante en perfil (pida que inicie sesión o complete manualmente)
            </div>
          )}
          {item.hasProfesional && (
            <div className="mt-1 text-[11px] text-emerald-700">
              Profesional: {item.especialidad || '—'} {item.comuna ? `· ${item.comuna}` : ''}
            </div>
          )}
        </div>

        {hasRoleDoc ? (
          <div className="flex flex-col items-end gap-1">
            <RoleBadge label="administrador" active={!!item.roles?.admin} />
            <RoleBadge label="trabajador" active={!!item.roles?.worker} />
            <RoleBadge label="usuario" active={!!item.roles?.user} />
          </div>
        ) : (
          <span className="text-xs text-slate-500">Sin roles (se asume user)</span>
        )}
      </div>

      <div className="mt-4 grid grid-cols-3 gap-3">
        <label className="flex items-center gap-2 text-sm col-span-3 sm:col-span-1">
          <input
            type="checkbox"
            className="h-4 w-4 accent-blue-600"
            checked={draft.admin}
            onChange={(e) => setDraft((d) => ({ ...d, admin: e.target.checked }))}
            disabled={saving || (!canDemote && !draft.admin)}
            title={!canDemote && !draft.admin ? 'No puedes retirar el único administrador' : ''}
          />
          Administrador
        </label>
        <label className="flex items-center gap-2 text-sm col-span-3 sm:col-span-1">
          <input
            type="checkbox"
            className="h-4 w-4 accent-blue-600"
            checked={draft.worker}
            onChange={(e) => setDraft((d) => ({ ...d, worker: e.target.checked }))}
            disabled={saving}
          />
          Trabajador
        </label>
        <label className="flex items-center gap-2 text-sm col-span-3 sm:col-span-1">
          <input
            type="checkbox"
            className="h-4 w-4 accent-blue-600"
            checked={draft.user}
            onChange={(e) => setDraft((d) => ({ ...d, user: e.target.checked }))}
            disabled={saving}
          />
          Usuario
        </label>
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
        {dirty && (
          <button
            onClick={() =>
              setDraft({
                admin: !!item.roles?.admin,
                worker: !!item.roles?.worker,
                user: item.roles ? !!item.roles.user : true,
              })
            }
            className="px-3 py-1.5 text-sm font-semibold border border-slate-300 rounded-xl bg-white hover:bg-slate-50"
            disabled={saving}
          >
            <RefreshCw className="inline-block mr-1" size={16} />
            Reset
          </button>
        )}
        <button
          onClick={() => onSaveRoles(item.uid, draft)}
          className={`px-3 py-1.5 text-sm rounded-xl font-semibold text-white flex items-center gap-1 shadow-sm ${
            dirty && item.uid 
            ? 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800' 
            : 'bg-blue-300 cursor-not-allowed'
          }`}
          disabled={!dirty || saving || !item.uid}
          title={!item.uid ? 'Este usuario no tiene uid en su perfil' : ''}
        >
          {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
          Guardar
        </button>
      </div>

      {hasRoleDoc && !canDemote && (
        <div className="mt-2 text-[11px] text-amber-600 flex items-center gap-1">
          <AlertTriangle size={12} /> Este es el único administrador; no puedes quitarte el rol.
        </div>
      )}
    </div>
  );
};

// ---------- Main ----------
export default function RolesAdminPage() {
  const [rolesByUid, setRolesByUid] = useState({});
  const [usuarios, setUsuarios] = useState([]);
  const [profByUid, setProfByUid] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingUid, setSavingUid] = useState(null);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [tab, setTab] = useState("todos");


  // Roles
  useEffect(() => {
    const q = query(collection(db, 'roles'), orderBy('admin', 'desc'), limit(500));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const obj = {};
        snap.forEach((d) => {
          const data = d.data() || {};
          obj[d.id] = { admin: !!data.admin, worker: !!data.worker, user: !!data.user };
        });
        setRolesByUid(obj);
      },
      (e) => {
        console.error(e);
        setError('No se pudieron cargar los roles.');
      }
    );
    return () => unsub();
  }, []);

  // Usuarios
  useEffect(() => {
    const q = query(collection(db, 'usuarios'), orderBy('correo'), limit(500));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const arr = [];
        snap.forEach((d) => {
          const data = d.data() || {};
          const correo = (data.correo || data.email || d.id || "").toLowerCase();

          const nombres = (data.nombres || "").trim();
          const apellidos = (data.apellidos || "").trim();

          // compat: si antes existía "nombre" o "displayName", úsalo como fallback
          const nombreCompleto =
            `${nombres} ${apellidos}`.trim() ||
            (data.nombre || "").trim() ||
            (data.displayName || "").trim();

          arr.push({
            emailId: d.id,
            email: correo,
            uid: (data.uid || "").trim(),
            nombre: nombreCompleto,
            displayName: (data.displayName || "").trim(),
            comuna: (data.comuna || "").trim(),
            region: (data.region || "").trim(),
          });
        });
        setUsuarios(arr);
        setLoading(false);
      },
      (e) => {
        console.error(e);
        setError('No se pudieron cargar los usuarios.');
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  // Profesionales (opcional)
  useEffect(() => {
    const q = query(collection(db, 'profesionales'), limit(500));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const obj = {};
        snap.forEach((d) => {
          const data = d.data() || {};
          obj[d.id] = {
            especialidad: data.especialidad || '',
            comuna: data.comuna || '',
            nombre: data.nombre || '',
          };
        });
        setProfByUid(obj);
      },
      (e) => {
        console.warn('Profesionales no disponible para lectura:', e?.message || e);
      }
    );
    return () => unsub();
  }, []);

  // Merge
  const merged = useMemo(() => {
    const list = usuarios.map((u) => {
      const roles = u.uid ? rolesByUid[u.uid] : undefined;
      const prof = u.uid ? profByUid[u.uid] : undefined;
      return {
        uid: u.uid || '',
        email: u.email || '',
        nombre: u.nombre || '',
        displayName: u.displayName || '',
        comuna: prof?.comuna || u.comuna || '',
        especialidad: prof?.especialidad || '',
        hasProfesional: !!prof,
        roles,
      };
    });

    Object.keys(rolesByUid).forEach((uid) => {
      const exists = list.some((x) => x.uid === uid);
      if (!exists) {
        list.push({
          uid,
          email: '',
          nombre: '',
          displayName: '',
          comuna: '',
          especialidad: '',
          hasProfesional: !!profByUid[uid],
          roles: rolesByUid[uid],
        });
      }
    });

    return list.sort((a, b) => {
      const ap = a.roles?.admin ? 0 : 1;
      const bp = b.roles?.admin ? 0 : 1;
      if (ap !== bp) return ap - bp;
      const A = (a.displayName || a.nombre || a.email || "").toString();
      const B = (b.displayName || b.nombre || b.email || "").toString();
      return A.localeCompare(B);
    });
  }, [usuarios, rolesByUid, profByUid]);

  const adminCount = useMemo(() => merged.filter((r) => r.roles?.admin).length, [merged]);
  const isLastAdmin = (uid) =>
    adminCount <= 1 && merged.find((r) => r.uid === uid)?.roles?.admin;

  // Guardar roles
  const handleSaveRoles = async (uid, draft) => {
    setError('');
    if (!uid) return;

    if (isLastAdmin(uid) && !draft.admin) {
      setError('No puedes quitar el rol administrador del último administrador. Crea otro primero.');
      return;
    }

    try {
      setSavingUid(uid);

      const ref = doc(db, 'roles', uid);
      const payload = {
        admin: !!draft.admin,
        worker: !!draft.worker,
        user: !!draft.user,
        updatedAt: serverTimestamp(),
      };

      // Guardar roles en /roles (si no existe lo crea, si existe lo actualiza)
      await setDoc(ref, payload, { merge: true });

      // Obtener datos del usuario para poblar profesionales
      const userInfo = merged.find((u) => u.uid === uid) || {};
      const email = userInfo.email || '';
      const nombre = (userInfo.nombre || userInfo.displayName || "").trim();

      // Sincronizar /profesionales
      await syncProfesional(uid, email, nombre, draft.worker);
    } catch (e) {
      console.error(e);
      setError('No se pudo guardar el cambio de roles. Revisa permisos y conexión.');
    } finally {
      setSavingUid(null);
    }
  };

  // Filtro búsqueda
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();

    let base = merged;

    // --- FILTRO POR TABS ---
    if (tab === "administrador") base = base.filter((u) => u.roles?.admin);
    else if (tab === "trabajador") base = base.filter((u) => u.roles?.worker);
    else if (tab === "usuario") base = base.filter((u) => u.roles?.user);
    else if (tab === "sinrol") base = base.filter((u) => !u.roles);

    // --- FILTRO POR BUSCADOR ---
    if (q) {
      base = base.filter((r) => {
        const s1 = r.uid?.toLowerCase() || '';
        const s2 = r.email?.toLowerCase() || '';
        const s3 = r.displayName?.toLowerCase() || r.nombre?.toLowerCase() || '';
        const s4 = r.especialidad?.toLowerCase() || '';
        return s1.includes(q) || s2.includes(q) || s3.includes(q) || s4.includes(q);
      });
    }

    return base;
  }, [merged, search, tab]);


  // ---- Wrappers seguros para usar useState en cada item ----
  const RowWrapper = (props) => {
    const { item } = props;
    const base = {
      admin: !!item.roles?.admin,
      worker: !!item.roles?.worker,
      // si no hay roles, por defecto user = true
      user: item.roles ? !!item.roles.user : true,
    };
    const [draft, setDraft] = useState(base);
    const dirty =
      draft.admin !== base.admin ||
      draft.worker !== base.worker ||
      draft.user !== base.user;

    return (
      <RoleRowDesktop
        {...props}
        draft={draft}
        setDraft={setDraft}
        dirty={dirty}
      />
    );
  };

  const CardWrapper = (props) => {
    const { item } = props;
    const base = {
      admin: !!item.roles?.admin,
      worker: !!item.roles?.worker,
      user: item.roles ? !!item.roles.user : true,
    };
    const [draft, setDraft] = useState(base);
    const dirty =
      draft.admin !== base.admin ||
      draft.worker !== base.worker ||
      draft.user !== base.user;

    return (
      <RoleCardMobile
        {...props}
        draft={draft}
        setDraft={setDraft}
        dirty={dirty}
      />
    );
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="max-w-6xl mx-auto">
          <header className="mb-6">

            <h1 className="text-2xl font-bold text-slate-900">Gestión de Roles</h1>
            <p className="text-sm text-slate-600">Asigna permisos de <strong>administrador</strong>, <strong>trabajador</strong> y{" "}<strong>usuario</strong>.</p>
          </header>
        {error && (
          <div className={warnBox}>
            <AlertTriangle className="mt-0.5" size={18} />
            <div className="text-sm">{error}</div>
          </div>
        )}

        {/* Tabs de roles */}
        <div className="flex flex-wrap gap-2 mb-4">
          {[
            { key: "todos", label: "Todos" },
            { key: "administrador", label: "Administrador" },
            { key: "trabajador", label: "Trabajador" },
            { key: "usuario", label: "Usuario" },
            { key: "sinrol", label: "Sin rol" },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`${tabBase} ${tab === t.key ? tabActive : tabIdle}`}
            >
              {t.label}
            </button>
          ))}
        </div>


        {/* Toolbar */}
        <div className={`${subCard} p-4 mb-5`}>
          <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3">
            <div className="relative w-full md:w-[420px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <input
                className={`${field} pl-10`}
                placeholder="Buscar por uid, email, nombre o especialidad…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <div className={`${badge} bg-white border-slate-200 text-slate-700`}>
              Administradores actuales: <span className="font-extrabold">{adminCount}</span>
            </div>
          </div>
        </div>

        {/* MOBILE (cards) */}
        <div className="md:hidden">
          {loading ? (
            <div className="p-4 text-center text-slate-500">
              <Loader2 className="inline-block animate-spin mr-2" /> Cargando…
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-4 text-center text-slate-500">Sin resultados.</div>
          ) : (
            filtered.map((item) => (
              <CardWrapper
                key={(item.uid || '') + (item.email || '')}
                item={item}
                hasRoleDoc={!!item.roles}
                canDemote={!(adminCount <= 1 && item.roles?.admin)}
                saving={savingUid === item.uid}
                onSaveRoles={handleSaveRoles}
              />
            ))
          )}
        </div>

        {/* DESKTOP (tabla) */}
        <div className={`hidden md:block ${card} overflow-hidden`}>
          <div className="overflow-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-slate-700 border-b border-slate-200/70">
                <tr>
                  <th className="text-left font-semibold px-4 py-3">Usuario</th>
                  <th className="text-left font-semibold px-4 py-3">Actual</th>
                  <th className="text-left font-semibold px-4 py-3">Editar</th>
                  <th className="text-right font-semibold px-4 py-3">Acciones</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-200/70">
                {loading ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-6 text-center text-slate-500">
                      <Loader2 className="inline-block animate-spin mr-2" /> Cargando…
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-6 text-center text-slate-500">
                      Sin resultados.
                    </td>
                  </tr>
                ) : (
                  filtered.map((item) => (
                    <RowWrapper
                      key={(item.uid || '') + (item.email || '')}
                      item={item}
                      hasRoleDoc={!!item.roles}
                      canDemote={!(adminCount <= 1 && item.roles?.admin)}
                      saving={savingUid === item.uid}
                      onSaveRoles={handleSaveRoles}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <footer className="mt-6 text-xs text-slate-500">
          Por defecto, los usuarios sin documento en <code>roles/</code> se tratan como{' '}
          <code>user</code>. Al modificar y guardar desde esta pantalla se creará o actualizará{' '}
          <code>roles/&lt;uid&gt;</code> automáticamente.
        </footer>
      </div>
    </div>
  );
}
